import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MessagePanel = ({ conversation, messages, onSendMessage, onFileUpload, currentUser }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const emojis = ['👍', '❤️', '😊', '😂', '😮', '😢', '😡', '🎉', '🔥', '💯'];

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage?.trim()) {
      onSendMessage({
        id: Date.now(),
        content: newMessage?.trim(),
        sender: currentUser,
        timestamp: new Date(),
        type: 'text'
      });
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const formatMessageTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (message) => {
    const isOwnMessage = message?.sender?.id === currentUser?.id;
    
    return (
      <div key={message?.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
        {!isOwnMessage && (
          <div className="flex-shrink-0 mr-3">
            <Image
              src={message?.sender?.avatar}
              alt={message?.sender?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          </div>
        )}
        <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-1' : 'order-2'}`}>
          {!isOwnMessage && (
            <p className="text-xs text-muted-foreground mb-1">{message?.sender?.name}</p>
          )}
          
          <div className={`rounded-lg px-4 py-2 ${
            isOwnMessage 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted text-foreground'
          }`}>
            {message?.type === 'text' && (
              <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
            )}
            
            {message?.type === 'file' && (
              <div className="flex items-center space-x-2">
                <Icon name="Paperclip" size={16} />
                <span className="text-sm">{message?.fileName}</span>
                <Button variant="ghost" size="sm">
                  <Icon name="Download" size={14} />
                </Button>
              </div>
            )}
            
            {message?.type === 'image' && (
              <div className="space-y-2">
                <Image
                  src={message?.imageUrl}
                  alt="Shared image"
                  className="rounded max-w-full h-auto"
                />
                {message?.content && (
                  <p className="text-sm">{message?.content}</p>
                )}
              </div>
            )}
          </div>
          
          <div className={`flex items-center mt-1 space-x-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <span className="text-xs text-muted-foreground">
              {formatMessageTime(message?.timestamp)}
            </span>
            {isOwnMessage && message?.status && (
              <Icon 
                name={message?.status === 'read' ? 'CheckCheck' : 'Check'} 
                size={12} 
                className={message?.status === 'read' ? 'text-primary' : 'text-muted-foreground'} 
              />
            )}
          </div>
          
          {message?.reactions && message?.reactions?.length > 0 && (
            <div className="flex space-x-1 mt-2">
              {message?.reactions?.map((reaction, index) => (
                <span key={index} className="text-xs bg-background border rounded px-2 py-1">
                  {reaction?.emoji} {reaction?.count}
                </span>
              ))}
            </div>
          )}
        </div>
        {isOwnMessage && (
          <div className="flex-shrink-0 ml-3">
            <Image
              src={message?.sender?.avatar}
              alt={message?.sender?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          </div>
        )}
      </div>
    );
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="MessageSquare" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Select a conversation</h3>
          <p className="text-muted-foreground">Choose a conversation from the sidebar to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-3">
          <div className="relative">
            {conversation?.avatar ? (
              <Image
                src={conversation?.avatar}
                alt={conversation?.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Icon name="Users" size={20} color="white" />
              </div>
            )}
            {conversation?.type === 'direct' && conversation?.isOnline && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-card" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-foreground">{conversation?.name}</h3>
            <p className="text-sm text-muted-foreground">
              {conversation?.type === 'direct' ? (conversation?.isOnline ?'Online' : 'Last seen recently')
                : `${conversation?.memberCount} members`
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon">
            <Icon name="Phone" size={20} />
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="Video" size={20} />
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="MoreVertical" size={20} />
          </Button>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map(renderMessage)}
        {isTyping && (
          <div className="flex items-center space-x-2 text-muted-foreground">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            <span className="text-sm">Someone is typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-border bg-card">
        <div className="flex items-end space-x-2">
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              className="pr-12"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="h-8 w-8"
              >
                <Icon name="Smile" size={16} />
              </Button>
            </div>
            
            {showEmojiPicker && (
              <div className="absolute bottom-full right-0 mb-2 bg-popover border border-border rounded-lg shadow-elevated p-2">
                <div className="grid grid-cols-5 gap-1">
                  {emojis?.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => handleEmojiSelect(emoji)}
                      className="p-2 hover:bg-muted rounded text-lg transition-micro"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef?.current?.click()}
          >
            <Icon name="Paperclip" size={20} />
          </Button>
          
          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!newMessage?.trim()}
          >
            <Icon name="Send" size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessagePanel;